import { createRouter, createWebHashHistory, type RouteLocationNormalized } from 'vue-router';
import { ElMessage } from 'element-plus';
import { hasPerm, session } from '@/core';
import { homePathFor, routes } from './routes';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { usePermissionStore, wirePermissionContext } from '@/stores/permission';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    crumb?: string;
    /** 菜单级权限码（取值见 routes.ts 注释，全部来自冻结权限清单） */
    perm?: string;
    menu?: boolean;
    menuOrder?: number;
    activeMenu?: string;
    public?: boolean;
  }
}

/** 与 admin-web 同用 hash 路由：外部端静态托管免服务端 rewrite，且端隔离可在网关按路径复核 */
export const router = createRouter({
  history: createWebHashHistory(import.meta.env.VITE_PUBLIC_PATH || '/'),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

wirePermissionContext();

function isPublic(route: RouteLocationNormalized): boolean {
  return route.matched.some((record) => record.meta.public === true);
}

/**
 * 去登录页。`?redirect=` 只回放**用户自己走出来的深链**。
 *
 * 站点根 `/` 是静态 redirect（routes.ts:26 → `/intake`），匿名访问时守卫看到的
 * `to.fullPath` 已经是 `/intake`，于是 URL 变成 `/login?redirect=%2Fintake`；
 * 这枚回跳不是用户的选择，却让机构管理员登录后又被打回录单页
 * （v1.4 §1.7 要求登录后按角色分流，落地页真源只有 `homePathFor`）。
 * vue-router 的 `to.redirectedFrom` 恰好标记「本次导航由路由级 redirect 产生」（含守卫自己返回的跳转）
 * ⇒ 这种一律不写 redirect，交由 LoginView 的 `safeRedirect()` 落回角色家页；
 * 直接深链（`#/mine`、`#/agency/team`，以及会话超时那两处自建 URL）行为不变。
 */
function toLogin(to: RouteLocationNormalized, reason?: string) {
  const query: Record<string, string> = {};
  if (!to.redirectedFrom) query.redirect = to.fullPath;
  if (reason) query.reason = reason;
  return { path: '/login', query };
}

/**
 * AC-6.7（t108 重写）：首登强制改密位的判据是**真信号** `user.forcePwdChange`
 * （后端登录响应第 5 键，两条登录腿都会下发；快照持久化 ⇒ 刷新后仍在）。
 * ⚠ 旧判据 `user.status === 'PASSWORD_CHANGE_REQUIRED'` 是**永不成立的分支**——后端从不下发该状态值
 * （t98/t108 全仓 0 命中），故与其文案一并下线；本端自建改密页（旧文案指向的「统一认证中心」四端皆无）。
 */
function needsPasswordChange(): boolean {
  return session.getSessionUser()?.forcePwdChange === true;
}

/** 会话超时前尝试保存本地草稿，避免 30min 到期丢整张表单 */
function trySaveDraftBeforeLogout(): void {
  try {
    const raw = window.localStorage.getItem('dh:bpo:intake:local-draft');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?.form?.customerPhone) {
      parsed.savedAt = Date.now();
      window.localStorage.setItem('dh:bpo:intake:local-draft', JSON.stringify(parsed));
    }
  } catch {
    /* 隐私模式或存储不可用，不阻塞登出 */
  }
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const app = useAppStore();
  const permission = usePermissionStore();

  if (isPublic(to)) {
    if (to.name === 'login' && auth.isAuthenticated) return { path: homePathFor(auth.user) };
    return true;
  }

  if (!session.getAccessToken()) return toLogin(to);

  if (!auth.user) auth.restore();
  if (!auth.user) return toLogin(to);

  // PRD AC-M1-3：会话 30min 无操作强制登出
  if (auth.expiresAt && auth.expiresAt < Date.now()) {
    trySaveDraftBeforeLogout();
    ElMessage.warning('会话已超时，请重新登录');
    auth.forceLogout();
    return toLogin(to, 'timeout');
  }

  // §6.2-7：刷新后先按 profile 重建权限态再判权限；端点不可用时沿用登录态快照并标记 stale
  if (auth.snapshotState === 'pending') await auth.syncProfile();

  // AC-6.7：首登强制改密 — 只放行改密页，其余业务视图一律阻断（不含任何录单入口）
  if (needsPasswordChange()) {
    if (to.name === 'changePassword') return true;
    return { path: '/change-password', query: { redirect: to.fullPath } };
  }

  // 端绑定互斥：管理端 / 小程序角色不得进入录单端
  if (!permission.endAllowed) {
    return { path: '/forbidden', query: { reason: 'end-binding' } };
  }

  if (to.meta.perm && !hasPerm(to.meta.perm)) {
    return { path: '/forbidden', query: { reason: 'perm', perm: to.meta.perm } };
  }

  app.setCrumb(to.meta.crumb || to.meta.title || '');
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} · 订单录入端` : '订单录入端 · 营销交付中控系统';
  if (document.title !== title) document.title = title;
});
